import { useMemo } from 'react';
import type { DataGridFilterState } from '@ibsys/kit';
import type { AllDetailsFiltersState } from '@/entities/dashboards';
import { BLOCK_COLUMN_PREFIX } from './useAllDetailsColumns';

type Params = {
  filters: AllDetailsFiltersState;
  onFiltersChange: (next: AllDetailsFiltersState) => void;
};

/**
 * Маппинг состояния фильтров между моделью страницы (`AllDetailsFiltersState`)
 * и состоянием DataGrid (`DataGridFilterState`). Возвращает значение для грида
 * и обработчик его изменений, преобразующий обратно в серверную модель.
 */
export const useAllDetailsFilters = ({ filters, onFiltersChange }: Params) => {
  const filtersForGrid = useMemo<DataGridFilterState>(() => {
    const out: DataGridFilterState = {};
    if (filters.externalId) out.externalId = { value: filters.externalId };
    if (filters.consultantName) out.consultant = { value: filters.consultantName };
    if (filters.clientName) out.clientName = { value: filters.clientName };
    const totalRange: { from?: number; to?: number } = {};
    if (filters.totalRatingFrom !== undefined) totalRange.from = filters.totalRatingFrom;
    if (filters.totalRatingTo !== undefined) totalRange.to = filters.totalRatingTo;
    if (Object.keys(totalRange).length > 0) out.totalScore = { value: totalRange };
    Object.entries(filters.blockRatings ?? {}).forEach(([blockIdRaw, range]) => {
      const blockId = Number(blockIdRaw);
      if (!Number.isFinite(blockId)) return;
      out[`${BLOCK_COLUMN_PREFIX}${blockId}`] = { value: range };
    });
    return out;
  }, [filters]);

  const handleFiltersChange = (next: DataGridFilterState) => {
    const result: AllDetailsFiltersState = {
      externalId: undefined,
      consultantName: undefined,
      clientName: undefined,
      totalRatingFrom: undefined,
      totalRatingTo: undefined,
      blockRatings: undefined,
    };
    const blockRatings: Record<number, { from?: number; to?: number }> = {};

    Object.entries(next).forEach(([columnId, filterValue]) => {
      const value = filterValue?.value;
      if (value === undefined || value === null || value === '') return;

      if (columnId === 'externalId') {
        result.externalId = String(value);
        return;
      }
      if (columnId === 'consultant') {
        result.consultantName = String(value);
        return;
      }
      if (columnId === 'clientName') {
        result.clientName = String(value);
        return;
      }
      if (columnId === 'totalScore' && typeof value === 'object') {
        const range = value as { from?: number; to?: number };
        if (range.from !== undefined) result.totalRatingFrom = Number(range.from);
        if (range.to !== undefined) result.totalRatingTo = Number(range.to);
        return;
      }
      if (columnId.startsWith(BLOCK_COLUMN_PREFIX) && typeof value === 'object') {
        const blockId = Number(columnId.slice(BLOCK_COLUMN_PREFIX.length));
        if (!Number.isFinite(blockId)) return;
        const range = value as { from?: number; to?: number };
        if (range.from === undefined && range.to === undefined) return;
        blockRatings[blockId] = range;
      }
    });

    if (Object.keys(blockRatings).length > 0) result.blockRatings = blockRatings;
    onFiltersChange(result);
  };

  return { filtersForGrid, handleFiltersChange };
};
